import {
  ExpoSpeechRecognitionModule,
  type ExpoSpeechRecognitionResultEvent,
  useSpeechRecognitionEvent,
} from "@-/com.npmjs.expo-speech-recognition";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { Alert } from "@-/com.npmjs.react-native";
import { useCallback, useEffect, useRef, useState } from "react";

import { subscribeToChineseSpeechTranscript } from "#chinese-speech-transcript-override";
import { isTestSpeechAudioEnabled, popTestSpeechAudio } from "#pop-test-speech-audio";
import { isTestSpeechEnabled, popTestSpeechTranscript } from "#pop-test-speech-transcript";
import { transcriptTailPastDispatched } from "#transcript-tail-past-dispatched";

// An iOS `start()` landing while the previous audio session is still tearing down throws
// transiently. Without a retry budget the mic silently dies partway through a drill and the user
// is left talking to nothing.
const MAX_START_RETRIES = 5;
const START_RETRY_DELAY_MS = 150;

export type ChineseSpeechCapture = {
  /** Everything heard since the last `markDispatched`, for a live "we heard…" readout. */
  heardText: string;
  isListening: boolean;
  /** Consume the transcript up to and including `text`, so later interims don't resubmit it. */
  markDispatched: (text: string) => void;
  /**
   * The recording being recognized in place of the microphone (`hanTestSpeechAudio`), so the
   * voice in it can be followed too; null when listening to the microphone.
   */
  recordingPath: string | null;
  /** Explicitly pause or resume, including cancelling a start waiting on permissions. */
  setListening: (isListening: boolean) => void;
  toggle: () => void;
};

/**
 * Capture Mandarin speech, streaming each new fragment to `onTranscript`.
 *
 * Everything here that looks incidental is load-bearing, and each piece is a bug the admin editor's
 * voice hook already paid for:
 *
 *   * **`lang: "zh-CN"`.** Every other call site in the app is hardcoded `en-US`; recognizing
 *     Mandarin with an English model returns noise.
 *   * **Top hypothesis only.** `event.results` is the recognizer's n-best ALTERNATIVES for one
 *     utterance, not sequential segments. Joining them concatenates competing guesses into a
 *     garbage string — and Mandarin's homophone density makes that far worse than in English.
 *   * **`contextualStrings`.** Biasing toward the target sentence's own words is the single
 *     cheapest accuracy win available, and it costs nothing to pass.
 *   * **Tail slicing.** One session grows ONE utterance, re-emitting everything on each interim.
 *     Without `transcriptTailPastDispatched` every tick would resubmit already-matched words.
 *   * **Auto-restart.** See {@link MAX_START_RETRIES}.
 */
export function useChineseSpeechCapture({
  contextualStrings,
  initialHeardText = "",
  onTranscript,
}: {
  contextualStrings: readonly string[];
  /** Retained display text; never resumes the microphone or re-dispatches a transcript. */
  initialHeardText?: string;
  onTranscript: (fragment: string) => void;
}): ChineseSpeechCapture {
  const [isListening, setIsListening] = useState(false);
  const [heardText, setHeardText] = useState(initialHeardText);
  const [recordingPath, setRecordingPath] = useState<string | null>(null);
  const recordingPathRef = useRef<string | null>(null);
  const mountedRef = useRef(true);
  const isListeningRef = useRef(false);
  const wantsListeningRef = useRef(false);
  const dispatchedRef = useRef("");
  const startRetryCountRef = useRef(0);
  // The callback is read from a ref so restarting the session never depends on the caller
  // memoizing it — a re-created `onTranscript` would otherwise tear down and rebuild the mic.
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;
  const contextualStringsRef = useRef(contextualStrings);
  contextualStringsRef.current = contextualStrings;

  const handleTranscript = useCallback((full: string) => {
    const tail = transcriptTailPastDispatched(full, dispatchedRef.current);
    setHeardText(tail);
    if (tail.length > 0) {
      onTranscriptRef.current(tail);
    }
  }, []);

  const markDispatched = useCallback((text: string) => {
    dispatchedRef.current = text;
    setHeardText("");
  }, []);

  // Always abort on unmount: an orphaned recognition session keeps the mic hot and the next
  // screen's `start()` fails against it.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      isListeningRef.current = false;
      wantsListeningRef.current = false;
      ExpoSpeechRecognitionModule.abort();
    };
  }, []);

  // The E2E/unit seam. Subscribed unconditionally (not only while listening) because a test drives
  // the drill without ever turning a microphone on.
  useEffect(() => subscribeToChineseSpeechTranscript(handleTranscript), [handleTranscript]);

  useSpeechRecognitionEvent("result", (event: ExpoSpeechRecognitionResultEvent) => {
    if (!isListeningRef.current) {
      return;
    }
    // `results[0]` ONLY — see the note above about n-best alternatives.
    const transcript = event.results[0]?.transcript ?? "";
    if (transcript.length > 0) {
      handleTranscript(transcript);
    }
  });

  useSpeechRecognitionEvent("error", (event) => {
    if (event.error === "aborted") {
      return;
    }
    logger(import.meta.location).warn(
      "speech: Chinese speech recognition error",
      event.error,
      event.message,
    );
    isListeningRef.current = false;
    wantsListeningRef.current = false;
    setIsListening(false);
  });

  const beginSession = useCallback(() => {
    if (!isListeningRef.current) {
      return;
    }
    const recording = recordingPathRef.current;
    try {
      ExpoSpeechRecognitionModule.start({
        audioSource: recording === null ? undefined : { uri: `file://${recording}` },
        contextualStrings: [...contextualStringsRef.current],
        continuous: recording === null,
        interimResults: true,
        lang: "zh-CN",
        maxAlternatives: 1,
      });
      startRetryCountRef.current = 0;
    } catch (error) {
      if (startRetryCountRef.current < MAX_START_RETRIES) {
        startRetryCountRef.current += 1;
        setTimeout(() => {
          beginSession();
        }, START_RETRY_DELAY_MS);
        return;
      }
      logger(import.meta.location).error(
        "speech: Chinese speech recognition failed to start",
        error,
      );
      isListeningRef.current = false;
      wantsListeningRef.current = false;
      setIsListening(false);
    }
  }, []);

  // A session ends on its own after a pause. Restart it while the user still means to be talking —
  // otherwise the mic dies the first time they stop to think mid-sentence.
  useSpeechRecognitionEvent("end", () => {
    if (isListeningRef.current) {
      // A recording has been transcribed once it ends; the mic stays live, hearing only silence.
      if (recordingPathRef.current === null) {
        beginSession();
      }
      return;
    }
    setIsListening(false);
  });

  const toggleMutation = useMutation({
    mutationFn: async (shouldListen: boolean) => {
      if (!shouldListen) {
        isListeningRef.current = false;
        setIsListening(false);
        recordingPathRef.current = null;
        setRecordingPath(null);
        if (!isTestSpeechEnabled()) {
          ExpoSpeechRecognitionModule.stop();
        }
        return;
      }
      if (isListeningRef.current || !wantsListeningRef.current) {
        return;
      }

      // A microphone cannot be emulated from an E2E run, so a queue of canned utterances supplied
      // at launch stands in for one: each activation POPS the next entry and replays it instead of
      // opening a recognition session. Popping (rather than replaying one fixed value) is what lets
      // a flow script a multi-turn drill — speak, get corrected, speak again — which is the whole
      // interaction these drills are built around.
      //
      // Checked BEFORE the permission prompt, which would otherwise block the flow on a system
      // dialog. Gated on `isTestSpeechEnabled` rather than on the pop result so an EXHAUSTED queue
      // still short-circuits: falling through to the real recognizer once the script runs out would
      // hang the flow on a permission dialog instead of failing on the missing utterance.
      if (isTestSpeechEnabled()) {
        isListeningRef.current = true;
        setIsListening(true);
        const testTranscript = popTestSpeechTranscript();
        // With a recording too, the mic is live while it plays, so the voice in it is followed while
        // the script stands in for what the recognizer heard (a simulator cannot recognize Mandarin).
        const recording = isTestSpeechAudioEnabled() ? popTestSpeechAudio() : null;
        if (recording !== null) {
          isListeningRef.current = true;
          recordingPathRef.current = recording;
          setRecordingPath(recording);
          setIsListening(true);
        }
        if (testTranscript !== null) {
          handleTranscript(testTranscript);
        }
        return;
      }

      // Recordings stand in for the voice itself (`hanTestSpeechAudio`): they are recognized for
      // real, so they go through the permission prompt and the recognizer like speech does.
      const recording = isTestSpeechAudioEnabled() ? popTestSpeechAudio() : null;

      const permission = await ExpoSpeechRecognitionModule.requestPermissionsAsync();
      // A permission result can arrive after the retained review released its native views.
      if (!mountedRef.current || !wantsListeningRef.current || isListeningRef.current) {
        return;
      }
      if (!permission.granted) {
        wantsListeningRef.current = false;
        Alert.alert(
          "Permission required",
          "Turn on microphone and speech recognition to answer by speaking.",
        );
        return;
      }

      isListeningRef.current = true;
      startRetryCountRef.current = 0;
      recordingPathRef.current = recording;
      setRecordingPath(recording);
      setIsListening(true);
      beginSession();
    },
    mutationKey: ["chineseSpeechToggle"],
  });

  const { mutate: changeListening } = toggleMutation;
  const setListening = useCallback(
    (shouldListen: boolean) => {
      wantsListeningRef.current = shouldListen;
      changeListening(shouldListen);
    },
    [changeListening],
  );
  const toggle = useCallback(() => setListening(!wantsListeningRef.current), [setListening]);

  return {
    heardText,
    isListening,
    markDispatched,
    recordingPath,
    setListening,
    toggle,
  };
}
